import cx from 'classnames';
import { Anchor, Button } from 'components/admin/ui';
import { getRequiredPermissionForPage, Restricted } from 'components/auth';
import { KebabMenu } from 'components/ui';
import { EthenaIcon, useWhiteLabelContext } from 'components/white-label';
import { ArrowNarrowLeft, LogOut01, Settings02 } from 'ethena-icons';
import { INTERNAL_PAGES } from 'lib/datasets/pages';
import { EnvVariable, getEnv } from 'lib/helpers/envHelper';
import { useToggle } from 'lib/hooks';
import _ from 'lodash';
import { useRouter } from 'next/router';
import { ElementType, useEffect, useRef, useState } from 'react';
import store from 'store2';
import css from './Navigation.module.scss';

interface INavigationLinkProps {
  href: string;
  icon: ElementType;
  label: string;
  isNew?: boolean;
}

const NavigationLink = ({
  href,
  icon: Icon,
  label,
  isNew = false,
}: INavigationLinkProps) => (
  <div className={css.navigationLinkContainer}>
    <Anchor aria-label={label} className={css.navigationAnchor} href={href}>
      <div className={css.iconContainer}>
        <Icon height={20} width={20} />
      </div>
      <span className={css.navigationLabel}>{label}</span>
      {isNew && <div className={css.newPill}>New</div>}
    </Anchor>
  </div>
);

export const Navigation = () => {
  const { pathname } = useRouter();

  const { organization } = useWhiteLabelContext();

  const nodeRef = useRef<HTMLElement>(null);
  const [isHelpMenuOpen, setIsHelpMenuOpen] = useState(false);
  const [showNavigationAnimation, toggleShowNavigationAnimation] =
    useToggle(true);

  const getIsCurrentPath = (path: string) => _.includes(pathname, path);

  const handleToggleNavigationAnimation = () => {
    toggleShowNavigationAnimation();
    store.set(
      'internalNavigationAnimationDisabled',
      showNavigationAnimation ? 'true' : 'false'
    );
  };

  useEffect(() => {
    if (store.get('internalNavigationAnimationDisabled') === 'true') {
      toggleShowNavigationAnimation();
    }
  }, []);

  const renderNavigationLink = (page: (typeof INTERNAL_PAGES)[number]) => {
    return (
      <Restricted
        key={page.name}
        toPermission={getRequiredPermissionForPage(page.name)}
      >
        <li aria-current={getIsCurrentPath(page.path)}>
          <NavigationLink
            icon={page.icon}
            href={page.path}
            label={page.getLabel({ organization })}
            isNew={false}
          />
        </li>
      </Restricted>
    );
  };

  return (
    <nav
      aria-label="Internal Tools navigation"
      className={cx(css.navigation, {
        [css.navigationAnimation]: showNavigationAnimation,
        [css.navigationHover]: isHelpMenuOpen && showNavigationAnimation,
      })}
      ref={nodeRef}
    >
      <div className={css.navigationHeaderLinkContainer}>
        <Anchor
          aria-label="Dashboard"
          className={css.navigationHeaderAnchor}
          href="/internal"
        >
          <div className={css.iconHeaderContainer}>
            <EthenaIcon className={css.logo} width={40} height={40} />
          </div>
          <span className={css.navigationHeaderLabel}>Internal Tools</span>
        </Anchor>
      </div>

      <div className={css.linksContainer}>
        <div>
          <ul className={css.links}>
            {_.map(INTERNAL_PAGES, renderNavigationLink)}
          </ul>
        </div>

        <ul className={cx(css.links, css.bordered)}>
          <li>
            <NavigationLink
              href={`/dashboard`}
              icon={ArrowNarrowLeft}
              label="Back to Admin Hub"
            />
          </li>
          <li
            className={cx({
              [css.linkHover]: isHelpMenuOpen && showNavigationAnimation,
            })}
          >
            <KebabMenu onOpen={setIsHelpMenuOpen}>
              <KebabMenu.Toggle>
                <Button className={css.helpButton} variant="transparent">
                  <div className={css.helpButtonContents}>
                    <div className={css.iconContainer}>
                      <Settings02 height={20} width={20} />
                    </div>
                    <span className={css.navigationLabel}>Settings</span>
                  </div>
                </Button>
              </KebabMenu.Toggle>
              <KebabMenu.Options placement="right-start">
                <KebabMenu.Option onClick={handleToggleNavigationAnimation}>
                  {showNavigationAnimation
                    ? 'Hide Navigation Titles'
                    : 'Show Navigation Titles'}
                </KebabMenu.Option>
              </KebabMenu.Options>
            </KebabMenu>
          </li>
          <li>
            <NavigationLink
              href={`${getEnv(EnvVariable.API_PATHNAME)}/logout`}
              icon={LogOut01}
              label="Logout"
            />
          </li>
        </ul>
      </div>
    </nav>
  );
};
